import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiClock } from 'react-icons/fi';
import {
  getMyCarrierDownloadRequests,
  type CarrierDownloadRequest,
} from '../../services/carrierDownloadService';

interface CarrierDownloadHistoryProps {
  refreshTrigger: number;
}

const STATUS_CONFIG: Record<
  CarrierDownloadRequest['status'],
  { label: string; color: string; bg: string; border: string }
> = {
  pending:     { label: 'Pending',     color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.3)'  },
  in_progress: { label: 'In Progress', color: '#60a5fa', bg: 'rgba(96,165,250,0.12)',  border: 'rgba(96,165,250,0.3)'  },
  completed:   { label: 'Completed',   color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.3)'   },
  failed:      { label: 'Failed',      color: '#ef4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.3)'   },
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

const CarrierDownloadHistory: React.FC<CarrierDownloadHistoryProps> = ({ refreshTrigger }) => {
  const [requests, setRequests] = useState<CarrierDownloadRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getMyCarrierDownloadRequests()
      .then((response) => setRequests(response.requests))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, [refreshTrigger]);

  if (loading) {
    return (
      <div className="cd-history">
        <div className="cd-history__skeleton">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="cd-history__skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  if (requests.length === 0) return null;

  return (
    <motion.div
      className="cd-history"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="cd-history__header">
        <FiClock size={14} />
        <span>My Requests</span>
      </div>

      <div className="cd-history__card glass-surface">
        {requests.map((req, i) => {
          const s = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
          return (
            <motion.div
              key={req.id}
              className="cd-history__row-inline"
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.04, duration: 0.2 }}
            >
              <span className="cd-history__row-label">Status</span>
              <span
                className="cd-history__status-badge"
                style={{ color: s.color, background: s.bg, borderColor: s.border }}
              >
                {s.label}
              </span>
              <span className="cd-history__row-label" style={{ marginLeft: 16 }}>Subscribed</span>
              <span
                className="cd-history__status-badge"
                style={
                  req.subscribed
                    ? { color: '#22c55e', background: 'rgba(34,197,94,0.12)', borderColor: 'rgba(34,197,94,0.3)' }
                    : { color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.1)' }
                }
              >
                {req.subscribed ? 'Yes' : 'No'}
              </span>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

export default CarrierDownloadHistory;